type AuthTokens = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
};

const keys = {
  accessToken: "auth.accessToken",
  refreshToken: "auth.refreshToken",
  idToken: "auth.idToken",
  user: "auth.user",
  activeOrgId: "auth.activeOrgId",
};

const noticeKeys = {
  authNotice: "auth.notice",
  postLoginPath: "auth.postLoginPath",
};

const storages: Storage[] = [sessionStorage, localStorage];
let activeOrgId: string | null = null;
let workspaceGeneration = 0;
const authChangeEvent = "auth:changed";

function notifyAuthChanged() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(authChangeEvent));
}

function getEnvOverride(key: string): string | undefined {
  const overrides = (globalThis as any).__IO_SPOKE_ENV as Record<string, string> | undefined;
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
    return overrides[key];
  }
  return undefined;
}

function isRefreshCookieEnabled() {
  const raw =
    getEnvOverride("VITE_REFRESH_COOKIE_ENABLED") ??
    import.meta.env.VITE_REFRESH_COOKIE_ENABLED;
  if (!raw) {
    return true;
  }
  return !["false", "0", "no"].includes(raw.toLowerCase());
}

export function useRefreshCookie() {
  return isRefreshCookieEnabled();
}

function getCsrfCookieName() {
  const override = getEnvOverride("VITE_AUTH_CSRF_COOKIE_NAME");
  const envValue = override ?? import.meta.env.VITE_AUTH_CSRF_COOKIE_NAME;
  return envValue || "io_spoke_csrf";
}

function getFromStorages(key: string) {
  for (const storage of storages) {
    const value = storage.getItem(key);
    if (value) {
      return { value, storage };
    }
  }
  return { value: null, storage: null };
}

function clearStorageKeys(storage: Storage) {
  Object.values(keys).forEach((key) => storage.removeItem(key));
}

export function getAccessToken() {
  return getFromStorages(keys.accessToken).value;
}

export function getRefreshToken() {
  if (isRefreshCookieEnabled()) {
    const stored = getFromStorages(keys.refreshToken);
    if (stored.value) {
      return stored;
    }
    return { value: null, storage: null };
  }
  return getFromStorages(keys.refreshToken);
}

export function getStoredUser() {
  const raw = getFromStorages(keys.user).value;
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as { name?: string; email?: string };
  } catch {
    return null;
  }
}

export function getActiveOrgId() {
  if (activeOrgId) {
    return activeOrgId;
  }
  const stored = getFromStorages(keys.activeOrgId).value;
  activeOrgId = stored;
  return stored;
}

export function getWorkspaceGeneration() {
  return workspaceGeneration;
}

export function setActiveOrgId(orgId?: string | null) {
  const normalized = orgId?.trim() || null;
  if (getActiveOrgId() !== normalized) workspaceGeneration += 1;
  activeOrgId = normalized;
  if (normalized) {
    sessionStorage.setItem(keys.activeOrgId, normalized);
    localStorage.setItem(keys.activeOrgId, normalized);
  } else {
    sessionStorage.removeItem(keys.activeOrgId);
    localStorage.removeItem(keys.activeOrgId);
  }
}

export function setAuthTokens(
  tokens: AuthTokens & { user?: { name?: string; email?: string } },
  rememberMe: boolean
) {
  setActiveOrgId(null);
  const target = rememberMe ? localStorage : sessionStorage;
  const other = rememberMe ? sessionStorage : localStorage;

  target.setItem(keys.accessToken, tokens.accessToken);
  if (!isRefreshCookieEnabled()) {
    if (tokens.refreshToken) {
      target.setItem(keys.refreshToken, tokens.refreshToken);
    } else {
      target.removeItem(keys.refreshToken);
    }
  } else {
    target.removeItem(keys.refreshToken);
  }
  if (tokens.idToken) {
    target.setItem(keys.idToken, tokens.idToken);
  } else {
    target.removeItem(keys.idToken);
  }
  if (tokens.user) {
    target.setItem(keys.user, JSON.stringify(tokens.user));
  } else {
    target.removeItem(keys.user);
  }

  clearStorageKeys(other);
  notifyAuthChanged();
}

export function setAuthTokensForStorage(storage: Storage, tokens: AuthTokens) {
  const other = storage === localStorage ? sessionStorage : localStorage;
  storage.setItem(keys.accessToken, tokens.accessToken);
  if (!isRefreshCookieEnabled() && tokens.refreshToken) {
    storage.setItem(keys.refreshToken, tokens.refreshToken);
  }
  if (tokens.idToken) {
    storage.setItem(keys.idToken, tokens.idToken);
  }
  clearStorageKeys(other);
  notifyAuthChanged();
}

export function clearAuthState() {
  workspaceGeneration += 1;
  clearStorageKeys(localStorage);
  clearStorageKeys(sessionStorage);
  activeOrgId = null;
  localStorage.removeItem("auth.pendingVerificationEmail");
  sessionStorage.removeItem("auth.pendingVerificationEmail");
  notifyAuthChanged();
}

export function setAuthNotice(message: string) {
  sessionStorage.setItem(noticeKeys.authNotice, message);
}

export function consumeAuthNotice() {
  const message = sessionStorage.getItem(noticeKeys.authNotice);
  if (message) {
    sessionStorage.removeItem(noticeKeys.authNotice);
    return message;
  }
  return null;
}

export function setPostLoginRedirectPath(pathname: string, search = "") {
  const safePathname = pathname.startsWith("/") ? pathname : `/${pathname}`;
  sessionStorage.setItem(noticeKeys.postLoginPath, `${safePathname}${search}`);
}

export function consumePostLoginRedirectPath() {
  const value = sessionStorage.getItem(noticeKeys.postLoginPath);
  if (value) {
    sessionStorage.removeItem(noticeKeys.postLoginPath);
    return value;
  }
  return null;
}

/** Read the pending redirect without consuming it, so mode selection can inspect it. */
export function peekPostLoginRedirectPath() {
  return sessionStorage.getItem(noticeKeys.postLoginPath);
}

export function getCsrfTokenFromCookie() {
  if (typeof document === "undefined") {
    return null;
  }
  const name = getCsrfCookieName();
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[-/\\^$*+?.()|[\\]{}]/g, "\\$&")}=([^;]+)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}
